import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { Search, ShoppingCart, ArrowUp, ArrowDown, Minus, X, Filter, ChevronLeft, ChevronRight, Check, XIcon, Trash2, Truck, RotateCcw, Bell, BellRing, Copy, Layers, Shuffle, Share2, UserPlus } from 'lucide-react';
import { userCanEditCrm } from '@/lib/crmApi';
import CrmNavLinks from '@/components/CrmNavLinks';
import { addProductToDraft } from '@/lib/customerOrderDraft';
import { useCatalogCustomerOrderDraft } from '@/hooks/useCatalogCustomerOrderDraft';
import CustomerOrderDraftPanel from '@/components/customer-orders/CustomerOrderDraftPanel';
import NotifyClientStockDialog from '@/components/catalog/NotifyClientStockDialog';
import BulkMatchModal from './home/BulkMatchModal';
import AltModal from './home/AltModal';
import ProdAltsModal from './home/ProdAltsModal';
import BuyLaterButton from './home/BuyLaterButton';
import PriceHistoryModal from './home/PriceHistoryModal';
import ProductDetailModal from './home/ProductDetailModal';
import ImageSearchModal from './home/ImageSearchModal';
import { useWebSocketContext } from '@/context/WebSocketContext';

import { getApiBaseUrl } from '@/lib/apiBase';

const API = getApiBaseUrl();

const formatBadgePrice = (price) =>
  Math.round(price || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

const formatStockStatusDate = (isoDate) => {
  if (!isoDate) return null;
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' });
};

const StockStatusDateLine = ({ isoDate }) => {
  const formatted = formatStockStatusDate(isoDate);
  return (
    <p className="text-xs opacity-80">
      {formatted ? `Статус с ${formatted}` : 'Дата смены статуса не зафиксирована'}
    </p>
  );
};

const HomePageNew = ({ embedded = false }) => {
  const { user, token, logout } = useAuth();

  // Read recreate params once — set by admin "recreate" button via ?_recreate=1&q=...
  const recreateParams = useRef(
    new URLSearchParams(window.location.search).get('_recreate') === '1'
      ? new URLSearchParams(window.location.search)
      : null
  ).current;

  // Interkom segments — loaded from API, not hardcoded
  const [interkomSegments, setInterkomSegments] = useState([]);

  // Subscribe to global WebSocket — toast shown by WebSocketContext, here we only refresh list
  const { subscribe } = useWebSocketContext();
  useEffect(() => {
    return subscribe((msg) => {
      if (msg.type === 'wish_list_available') {
        setPage(1);
        setProducts([]);
      }
      if (msg.type === 'buy_later_due') {
        setPage(1);
        setProducts([]);
      }
    });
  }, [subscribe]);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [search, setSearch] = useState(recreateParams?.get('q') || '');
  const [searchInput, setSearchInput] = useState(recreateParams?.get('q') || '');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showPriceType, setShowPriceType] = useState({});
  const [refreshingProducts, setRefreshingProducts] = useState({});
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [notifyClientProduct, setNotifyClientProduct] = useState(null);
  const [sharePopupOpen, setSharePopupOpen] = useState(false);
  const [shareRecipient, setShareRecipient] = useState('');
  const [sharePhone, setSharePhone] = useState('');
  const [shareDiscount, setShareDiscount] = useState('12');
  const [shareDeliveryDays, setShareDeliveryDays] = useState('1');
  const [shareCreating, setShareCreating] = useState(false);
  const [shareWaSending, setShareWaSending] = useState(false);
  const [shareWaSent, setShareWaSent] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());
  // Alt modal state — only for main product-row alternatives (bulk has its own internal alt modal)
  const [altModalOpen, setAltModalOpen] = useState(false);
  const [altOptions, setAltOptions] = useState([]);
  const [altCurrentId, setAltCurrentId] = useState(null);
  const [altMainLoading, setAltMainLoading] = useState(false);
  const [prodAltsModalOpen, setProdAltsModalOpen] = useState(false);
  const [prodAltsProduct, setProdAltsProduct] = useState(null);
  const [prodAltsOffers, setProdAltsOffers] = useState([]);
  const [prodAltsLoading, setProdAltsLoading] = useState(false);
  const [priceHistoryProduct, setPriceHistoryProduct] = useState(null);
  const [filtersReady, setFiltersReady] = useState(false);
  const initialFetchDone = useRef(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageProgress, setImageProgress] = useState(0);
  const [linkedOffers, setLinkedOffers] = useState([]);
  const [linkedBase, setLinkedBase] = useState(null);
  const [linkedLoading, setLinkedLoading] = useState(false);
  const searchInputRef = useRef(null);
  const barcodeBufferRef = useRef('');
  const barcodeTimeoutRef = useRef(null);
  const lastBarcodeKeyTimeRef = useRef(0);
  const imageRetryRef = useRef(new Set());
  const [prefetching, setPrefetching] = useState(false);
  const prefetchedPagesRef = useRef({});
  // Invalidates in-flight product/prefetch responses when search/filters/sort change
  const fetchGenerationRef = useRef(0);
  const altCountsGenerationRef = useRef(0);
  const [imageSearchProduct, setImageSearchProduct] = useState(null);
  
  // Filters
  const [suppliers, setSuppliers] = useState([]);
  const [interkomSupplierId, setInterkomSupplierId] = useState(null);
  const [selectedSuppliers, setSelectedSuppliers] = useState(() => {
    const s = recreateParams?.get('suppliers');
    return s ? s.split(',').filter(Boolean) : [];
  });
  const [selectedSegments, setSelectedSegments] = useState(() => {
    const c = recreateParams?.get('category');
    return c ? [c] : [];
  });
  const [inStockOnly, setInStockOnly] = useState(
    recreateParams ? recreateParams.get('inStock') === '1' : true
  );
  const [brandOptions, setBrandOptions] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState(() => {
    const b = recreateParams?.get('brands');
    return b ? b.split(',').filter(Boolean) : [];
  });
  const [newOnly, setNewOnly] = useState(recreateParams ? recreateParams.get('newOnly') === '1' : false);
  const [backOnStockOnly, setBackOnStockOnly] = useState(recreateParams ? recreateParams.get('backOnStock') === '1' : false);
  const [wishListOnly, setWishListOnly] = useState(false); // "Ожидаются"
  const [buyLaterOnly, setBuyLaterOnly] = useState(false); // "В отлежке"
  const [filterCounts, setFilterCounts] = useState({ in_stock: 0, wish_list: 0, buy_later: 0, new: 0, back_on_stock: 0 });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  // Sorting
  const [sortBy, setSortBy] = useState(recreateParams?.get('sortBy') || 'name');
  const [sortOrder, setSortOrder] = useState(recreateParams?.get('sortOrder') || 'asc');
  
  // Quantities and Cart
  const [quantities, setQuantities] = useState({});
  const [cart, setCart] = useState(null);
  const [openOrderProductMap, setOpenOrderProductMap] = useState(() => new Map());
  const [cartsBySupplier, setCartsBySupplier] = useState({});
  const [showPurchasePrice, setShowPurchasePrice] = useState(false); // Toggle for price display
  const [activeCartTab, setActiveCartTab] = useState(null); // Track active cart tab
  const [bottomPanelTab, setBottomPanelTab] = useState('suppliers');
  const canEditCrm = userCanEditCrm(user);
  const {
    draft: customerOrderDraft,
    setDraft: setCustomerOrderDraft,
    resetDraft: resetCustomerOrderDraft,
    hydrated: customerOrderDraftHydrated,
  } = useCatalogCustomerOrderDraft({
    user,
    token,
    enabled: canEditCrm,
  });
  const observer = useRef();
  const [barcodeSearchCode, setBarcodeSearchCode] = useState(null);
  const cartProductQtyMap = useMemo(() => {
    const map = new Map();
    if (cart?.items) {
      cart.items.forEach((item) => {
        if (item.product_id) {
          map.set(item.product_id, (map.get(item.product_id) || 0) + item.quantity);
        }
      });
    }
    return map;
  }, [cart]);

  const customerOrderProductQtyMap = useMemo(() => {
    const map = new Map();
    customerOrderDraft.items.forEach((item) => {
      if (item.product_id) {
        map.set(item.product_id, (map.get(item.product_id) || 0) + item.quantity);
      }
    });
    return map;
  }, [customerOrderDraft.items]);

  const hasSupplierCart = !!(cart?.items?.length);
  const hasCustomerDraft =
    canEditCrm && customerOrderDraftHydrated && customerOrderDraft.items.length > 0;
  const showBottomPanel = !!user && (hasSupplierCart || hasCustomerDraft);

  useEffect(() => {
    if (!hasSupplierCart && hasCustomerDraft && canEditCrm) {
      setBottomPanelTab('customer-order');
    } else if (!hasCustomerDraft && bottomPanelTab === 'customer-order' && hasSupplierCart) {
      setBottomPanelTab('suppliers');
    }
  }, [hasSupplierCart, hasCustomerDraft, canEditCrm, bottomPanelTab]);

  const lastProductRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prevPage) => prevPage + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  // Clean URL and notify manager when opened from admin "recreate"
  useEffect(() => {
    if (recreateParams) {
      window.history.replaceState({}, '', '/');
      toast.info('Подборка восстановлена — создайте новую ссылку для клиента');
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    try {
      const cached = localStorage.getItem('suppliers_cache');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setSuppliers(parsed);
        }
      }
    } catch (e) {
      // ignore cache errors
    }

    fetchSuppliers();
    if (user) {
      fetchCart();
      fetchOpenOrders();
    } else {
      setCart(null);
      setCartsBySupplier({});
      setOpenOrderProductMap(new Map());
      setActiveCartTab(null);
    }
  }, [user]);

  // Drop prefetched pages as soon as catalog query changes (not on page alone),
  // so infinite scroll never appends results from a previous search/filter.
  useEffect(() => {
    prefetchedPagesRef.current = {};
    fetchGenerationRef.current += 1;
    altCountsGenerationRef.current += 1;
    setPrefetching(false);
  }, [search, selectedSuppliers, selectedBrands, selectedSegments, inStockOnly, newOnly, backOnStockOnly, wishListOnly, buyLaterOnly, sortBy, sortOrder]);

  useEffect(() => {
    if (!filtersReady) return; // не грузим товары пока не установились стартовые фильтры
    if (!initialFetchDone.current) {
      initialFetchDone.current = true;
      fetchProducts();
      return;
    }
    fetchProducts();
  }, [page, search, selectedSuppliers, selectedBrands, selectedSegments, inStockOnly, newOnly, backOnStockOnly, wishListOnly, buyLaterOnly, sortBy, sortOrder, filtersReady]);

  // Debounced search: отправляем запрос через 300 мс бездействия, либо мгновенно при явном апдейте
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchInput !== search) {
        setSearch(searchInput);
        setPage(1);
        setProducts([]);
        setSelectedItems(new Set());
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [searchInput, search]);

  const fetchSuppliers = async () => {
    try {
      const [suppliersResp, segmentsResp] = await Promise.allSettled([
        axios.get(`${API}/suppliers`),
        axios.get(`${API}/interkom/segments`),
      ]);

      if (suppliersResp.status === 'fulfilled') {
        const data = suppliersResp.value.data;
        setSuppliers(data);
        try { localStorage.setItem('suppliers_cache', JSON.stringify(data)); } catch {}
        const interkom = data.find((s) => s.name?.toLowerCase().includes('интерком'));
        setInterkomSupplierId(interkom ? interkom.id : null);
        if (interkom && selectedSuppliers.length === 0 && !initialFetchDone.current && !recreateParams) {
          setSelectedSuppliers([interkom.id]);
          setPage(1);
          setProducts([]);
        }
      }

      if (segmentsResp.status === 'fulfilled') {
        setInterkomSegments(segmentsResp.value.data?.segments || []);
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    } finally {
      setFiltersReady(true);
    }
  };

  const getApiErrorMessage = (error, fallback = 'Ошибка запроса') => {
    const detail = error?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail.map((item) => item?.msg || JSON.stringify(item)).join('; ');
    }
    if (detail && typeof detail === 'object') {
      return detail.message || detail.msg || [detail.error, detail.error_type].filter(Boolean).join(' — ') || JSON.stringify(detail);
    }
    return error?.message || fallback;
  };

  const getPackingRate = (value) => {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) || parsed < 1 ? 1 : parsed;
  };

  const normalizeQuantityWithPacking = (quantity, packingRate) => {
    const parsedQty = parseInt(quantity, 10);
    const safeQty = Number.isNaN(parsedQty) || parsedQty < 1 ? 1 : parsedQty;
    return Math.max(safeQty, getPackingRate(packingRate));
  };

  const getCatalogFilterParams = useCallback((overrides = {}) => {
    const params = {
      search: search || undefined,
      in_stock_only: inStockOnly,
      new_only: newOnly,
      back_on_stock_only: backOnStockOnly,
      wish_list_only: wishListOnly,
      buy_later_only: buyLaterOnly,
      ...overrides,
    };
    if (selectedSuppliers.length > 0) {
      params.supplier_ids = selectedSuppliers.join(',');
    }
    if (selectedBrands.length > 0) {
      params.brands = selectedBrands.join(',');
    }
    const interkomOnly = interkomSupplierId && selectedSuppliers.length === 1 && selectedSuppliers[0] === interkomSupplierId;
    if (selectedSegments.length > 0 && interkomOnly) {
      params.category = selectedSegments[0];
    }
    return params;
  }, [
    search,
    inStockOnly,
    newOnly,
    backOnStockOnly,
    wishListOnly,
    buyLaterOnly,
    selectedSuppliers,
    selectedBrands,
    selectedSegments,
    interkomSupplierId,
  ]);

  const getCatalogFilterBody = useCallback(() => {
    const body = {
      search: search || undefined,
      new_only: newOnly,
      back_on_stock_only: backOnStockOnly,
      wish_list_only: wishListOnly,
      buy_later_only: buyLaterOnly,
      in_stock_only: inStockOnly,
    };
    if (selectedSuppliers.length > 0) {
      body.supplier_ids = selectedSuppliers;
    }
    if (selectedBrands.length > 0) {
      body.brands = selectedBrands;
    }
    const interkomOnly = interkomSupplierId && selectedSuppliers.length === 1 && selectedSuppliers[0] === interkomSupplierId;
    if (selectedSegments.length > 0 && interkomOnly) {
      body.category = selectedSegments[0];
    }
    return body;
  }, [
    search,
    inStockOnly,
    newOnly,
    backOnStockOnly,
    wishListOnly,
    buyLaterOnly,
    selectedSuppliers,
    selectedBrands,
    selectedSegments,
    interkomSupplierId,
  ]);

  const getBulkMatchFilterBody = useCallback(() => {
    const { in_stock_only: _inStockOnly, ...body } = getCatalogFilterBody();
    return body;
  }, [getCatalogFilterBody]);

  // Альтернативы по OEM не должны наследовать поиск/бренды/статусы каталога —
  // иначе при поиске по артикулу бренда в модалке остаётся только текущая позиция.
  const getAltModalFilterBody = useCallback(() => {
    const body = getBulkMatchFilterBody();
    const {
      search: _search,
      brands: _brands,
      new_only: _newOnly,
      back_on_stock_only: _backOnStockOnly,
      wish_list_only: _wishListOnly,
      buy_later_only: _buyLaterOnly,
      ...scope
    } = body;
    return scope;
  }, [getBulkMatchFilterBody]);

  const fetchAltCountsForProducts = useCallback(async (productList) => {
    if (!token || !productList?.length) return;
    const items = productList
      .map((p) => {
        const articul = (p.oem || p.articul || '').trim();
        if (!p.id || !articul) return null;
        return { id: p.id, articul };
      })
      .filter(Boolean);
    if (!items.length) return;

    const generation = ++altCountsGenerationRef.current;
    try {
      const { data } = await axios.post(
        `${API}/products/alt-counts`,
        { items, ...getAltModalFilterBody() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (generation !== altCountsGenerationRef.current) return;
      const counts = data?.counts || {};
      setProducts((prev) => {
        let changed = false;
        const next = prev.map((p) => {
          if (counts[p.id] == null || p.alt_count === counts[p.id]) return p;
          changed = true;
          return { ...p, alt_count: counts[p.id] };
        });
        return changed ? next : prev;
      });
    } catch {
      // Не блокируем каталог — бейдж просто не появится.
    }
  }, [token, getAltModalFilterBody]);

  const fetchProducts = async () => {
    const generation = fetchGenerationRef.current;
    if (page === 1) {
      prefetchedPagesRef.current = {};
    }
    setLoading(true);
    try {
      const params = {
        page,
        page_size: 50,
        sort_by: sortBy,
        sort_order: sortOrder,
        ...getCatalogFilterParams(),
      };
      
      let incomingProducts = [];
      let response;
      if (prefetchedPagesRef.current[page]) {
        incomingProducts = prefetchedPagesRef.current[page].products;
        response = { data: prefetchedPagesRef.current[page].response };
        delete prefetchedPagesRef.current[page];
      } else {
        response = await axios.get(`${API}/products`, { params });
        if (generation !== fetchGenerationRef.current) return;
        incomingProducts = response.data.products || [];
      }

      if (generation !== fetchGenerationRef.current) return;
      
      if (page === 1) {
        setProducts(incomingProducts);
        const brandsFromProducts = Array.from(new Set(incomingProducts.map((p) => p.brand).filter(Boolean)));
        setBrandOptions(brandsFromProducts);
        if (response.data.filter_counts) {
          setFilterCounts(response.data.filter_counts);
        }
      } else {
        setProducts((prev) => [...prev, ...incomingProducts]);
        const newBrands = incomingProducts.map((p) => p.brand).filter(Boolean);
        if (newBrands.length > 0) {
          setBrandOptions((prev) => Array.from(new Set([...prev, ...newBrands])));
        }
        if (response.data.filter_counts) {
          setFilterCounts(response.data.filter_counts);
        }
      }
      
      const hasMoreFromApi = Boolean(response.data.has_more);
      const hasMoreLocal = incomingProducts.length > 0 && hasMoreFromApi;
      setHasMore(hasMoreLocal);

      // Счётчики альтернатив — после рендера списка, не тормозят выдачу каталога.
      fetchAltCountsForProducts(incomingProducts);

      const nextPage = page + 1;
      if (hasMoreLocal && !prefetchedPagesRef.current[nextPage] && !prefetching) {
        setPrefetching(true);
        try {
          const nextResp = await axios.get(`${API}/products`, { params: { ...params, page: nextPage } });
          if (generation !== fetchGenerationRef.current) return;
          prefetchedPagesRef.current[nextPage] = {
            products: nextResp.data.products || [],
            response: nextResp.data,
          };
        } catch (prefetchErr) {
        } finally {
          if (generation === fetchGenerationRef.current) {
            setPrefetching(false);
          }
        }
      }

      // Если запрос был после сканирования, сразу открываем найденный товар
      if (barcodeSearchCode && page === 1) {
        const normalizeBarcode = (val) => (val || '').replace(/\s+/g, '').toLowerCase();
        const target = normalizeBarcode(barcodeSearchCode);
        const matched = incomingProducts.find((p) => {
          const barcodes = Array.isArray(p.barcodes) ? p.barcodes : [];
          if (barcodes.some((b) => normalizeBarcode(b) === target)) return true;
          if (normalizeBarcode(p.code) === target) return true;
          if (normalizeBarcode(p.articul) === target) return true;
          return false;
        });
        if (matched) {
          setSelectedProduct(matched);
          setActiveImageIndex(0);
          const shouldFetchImages = (!matched.images || matched.images.length === 0) && !matched.image_unavailable;
          if (shouldFetchImages) {
            fetchImagesForProduct(matched.id);
          }
        }
        setBarcodeSearchCode(null);
      }
    } catch (error) {
      if (generation !== fetchGenerationRef.current) return;
      const msg = getApiErrorMessage(error, 'Ошибка загрузки товаров');
      toast.error(msg);
    } finally {
      if (generation === fetchGenerationRef.current) {
        setLoading(false);
      }
    }
  };

  const fetchCart = async () => {
    if (!user || !token) return;
    
    try {
      const response = await axios.get(`${API}/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCart(response.data);
      
      // Group cart items by supplier using supplier_name from backend
      const grouped = {};
      response.data.items.forEach(item => {
        const supplierName = item.supplier_name || 'Неизвестно';
        
        if (!grouped[supplierName]) {
          grouped[supplierName] = [];
        }
        grouped[supplierName].push(item);
      });
      
      setCartsBySupplier(grouped);
      
      // Set initial active tab
      if (Object.keys(grouped).length > 0 && !activeCartTab) {
        setActiveCartTab(Object.keys(grouped)[0]);
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  };

  const fetchOpenOrders = async () => {
    if (!user || !token) return;

    try {
      const { data } = await axios.get(`${API}/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const productMap = new Map();
      (data?.orders || []).forEach((order) => {
        if (order?.status === 'Новый' || order?.status === 'В обработке') {
          (order.items || []).forEach((item) => {
            if (!item?.product_id) return;
            const existing = productMap.get(item.product_id);
            if (existing) {
              existing.quantity = (existing.quantity || 0) + (item.quantity || 0);
            } else {
              productMap.set(item.product_id, {
                order_id: order.id,
                order_number: order.order_number,
                quantity: item.quantity || 0,
              });
            }
          });
        }
      });
      setOpenOrderProductMap(productMap);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const handleSearchChange = (value) => {
    setSearchInput(value);
    if (value.endsWith(' ') && value !== search) {
      setSearch(value);
      setPage(1);
      setProducts([]);
    }
  };

  const handleSearchClear = () => {
    setSearchInput('');
    if (search !== '') {
      setSearch('');
      setPage(1);
      setProducts([]);
    }
  };

  useEffect(() => {
    const handleGlobalPaste = (event) => {
      const target = event.target;
      const isTextTarget =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target?.isContentEditable;

      if (isTextTarget) return;

      const pasted = event.clipboardData?.getData('text')?.trim();
      if (!pasted) return;

      event.preventDefault();
      searchInputRef.current?.focus();
      setSearchInput(pasted);
      setSearch(pasted);
      setPage(1);
      setProducts([]);
    };

    window.addEventListener('paste', handleGlobalPaste);
    return () => window.removeEventListener('paste', handleGlobalPaste);
  }, []);

  const applyBarcodeSearch = useCallback((code) => {
    const cleaned = code.trim();
    if (!cleaned) return;
    searchInputRef.current?.focus();
    setSelectedProduct(null);
    setActiveImageIndex(0);
    setSearchInput(cleaned);
    setSearch(cleaned);
    setSelectedSuppliers([]); // ищем по всем поставщикам
    setSelectedSegments([]);
    setInStockOnly(false); // штрихкоды могут быть у товаров без остатка
    setBarcodeSearchCode(cleaned);
    setPage(1);
    setProducts([]);
    toast.success(`Штрихкод ${cleaned} — ищем товар`);
  }, []);

  useEffect(() => {
    const resetBarcodeBuffer = () => {
      barcodeBufferRef.current = '';
      if (barcodeTimeoutRef.current) {
        clearTimeout(barcodeTimeoutRef.current);
        barcodeTimeoutRef.current = null;
      }
    };

    const finalizeBarcode = () => {
      if (barcodeBufferRef.current.length >= 4) {
        applyBarcodeSearch(barcodeBufferRef.current);
      }
      resetBarcodeBuffer();
    };

    const handleBarcodeKeydown = (event) => {
      const target = event.target;
      const isSearchInput = searchInputRef.current && target === searchInputRef.current;
      const isTextTarget =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target?.isContentEditable;
      if (!isSearchInput && isTextTarget) return;

      const now = Date.now();
      const isNewScan = now - lastBarcodeKeyTimeRef.current > 120;
      if (isNewScan) {
        barcodeBufferRef.current = '';
      }
      lastBarcodeKeyTimeRef.current = now;

      if (event.key === 'Enter') {
        if (barcodeBufferRef.current) {
          finalizeBarcode();
          event.preventDefault();
        }
        return;
      }

      if (event.key.length === 1 && /^[0-9A-Za-z]$/.test(event.key)) {
        barcodeBufferRef.current += event.key;
        if (barcodeTimeoutRef.current) {
          clearTimeout(barcodeTimeoutRef.current);
        }
        barcodeTimeoutRef.current = setTimeout(finalizeBarcode, 150);
      }
    };

    window.addEventListener('keydown', handleBarcodeKeydown);
    return () => {
      window.removeEventListener('keydown', handleBarcodeKeydown);
      if (barcodeTimeoutRef.current) {
        clearTimeout(barcodeTimeoutRef.current);
      }
    };
  }, [applyBarcodeSearch]);

  const handleFilterChange = () => {
    if (!filtersReady) return; // пока не получили стартовые фильтры, не дёргаем лишний fetch
    setPage(1);
    setProducts([]);
  };

  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(1);
    setProducts([]);
  };

  const toggleSupplier = (supplierId) => {
    setSelectedSuppliers(prev => {
      const next = prev.includes(supplierId)
        ? prev.filter(id => id !== supplierId)
        : [...prev, supplierId];

      // Сегменты доступны только для единственного поставщика Интерком
      const interkomOnly = interkomSupplierId && next.length === 1 && next[0] === interkomSupplierId;
      if (!interkomOnly && selectedSegments.length > 0) {
        setSelectedSegments([]);
      }

      return next;
    });
    handleFilterChange();
  };

  const toggleBrand = (brand) => {
    setSelectedBrands(prev => {
      if (prev.includes(brand)) {
        return prev.filter(b => b !== brand);
      }
      return [...prev, brand];
    });
    handleFilterChange();
  };

  const toggleSegment = (segmentId) => {
    setSelectedSegments((prev) => (prev.includes(segmentId) ? [] : [segmentId]));
    handleFilterChange();
  };

  const clearFilters = () => {
    setSelectedSuppliers([]);
    setSelectedSegments([]);
    setSelectedBrands([]);
    setWishListOnly(false);
    setBuyLaterOnly(false);
    setInStockOnly(false);
    setNewOnly(false);
    setBackOnStockOnly(false);
    handleFilterChange();
  };

  const addToCart = async (productId, quantity = 1) => {
    if (!user) {
      toast.error('Войдите в систему для добавления в корзину');
      return;
    }

    try {
      await axios.post(
        `${API}/cart/items`,
        { product_id: productId, quantity: quantity },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Товар добавлен в корзину (${quantity} шт)`);
      fetchCart();
    } catch (error) {
      toast.error('Ошибка добавления в корзину');
    }
  };

  const addToCustomerOrder = (product, quantity = 1) => {
    if (!user) {
      toast.error('Войдите в систему');
      return;
    }
    if (!canEditCrm) {
      toast.error('Нет доступа к заказам клиентов');
      return;
    }

    setCustomerOrderDraft((prev) => addProductToDraft(prev, product, quantity));
    setBottomPanelTab('customer-order');
    toast.success(`Товар добавлен в заказ клиента (${quantity} шт)`);
  };

  const updateCartItem = async (productId, quantity) => {
    try {
      await axios.put(
        `${API}/cart/items/${productId}?quantity=${quantity}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchCart();
    } catch (error) {
      toast.error('Ошибка обновления количества');
    }
  };

  const removeFromCart = async (productId) => {
    try {
      await axios.delete(`${API}/cart/items/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Товар удален из корзины');
      fetchCart();
    } catch (error) {
      toast.error('Ошибка удаления товара');
    }
  };

  const clearCart = async () => {
    if (!activeCartTab) {
      toast.error('Выберите корзину поставщика');
      return;
    }
    const items = cartsBySupplier[activeCartTab] || [];
    if (!items.length) {
      toast.error('Корзина уже пуста');
      return;
    }
    const supplierId = items[0]?.supplier_id;
    try {
      await axios.delete(`${API}/cart`, {
        headers: { Authorization: `Bearer ${token}` },
        params: supplierId ? { supplier_id: supplierId } : {},
      });
      toast.success('Корзина очищена');
      fetchCart();
    } catch (error) {
      toast.error('Не удалось очистить корзину');
    }
  };

  const togglePriceType = (productId) => {
    setShowPriceType((prev) => ({ ...prev, [productId]: !prev[productId] }));
  };

  const toggleWishList = async (productId, currentValue) => {
    if (!user || !token) {
      toast.error('Войдите, чтобы управлять избранным');
      throw new Error('not authenticated');
    }
    const newValue = !currentValue;
    const applyWishList = (list) =>
      list.map((p) => (p.id === productId ? { ...p, wish_list: newValue } : p));

    if (wishListOnly && currentValue) {
      setProducts((prev) => prev.filter((p) => p.id !== productId));
    } else {
      setProducts(applyWishList);
    }
    setSelectedProduct((prev) =>
      prev && prev.id === productId ? { ...prev, wish_list: newValue } : prev
    );

    try {
      await axios.patch(
        `${API}/products/${productId}/wish-list`,
        { wish_list: newValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      if (wishListOnly && currentValue) {
        fetchProducts();
      } else {
        setProducts((prev) =>
          prev.map((p) => (p.id === productId ? { ...p, wish_list: currentValue } : p))
        );
      }
      setSelectedProduct((prev) =>
        prev && prev.id === productId ? { ...prev, wish_list: currentValue } : prev
      );
      toast.error('Не удалось обновить избранное');
      throw error;
    }
  };

  const setBuyLater = async (productId, days) => {
    if (!user || !token) {
      toast.error('Войдите, чтобы добавить в отлежку');
      return;
    }
    try {
      const response = await axios.patch(
        `${API}/products/${productId}/buy-later`,
        { buy_later: true, days },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const updated = response.data;
      setProducts((prev) =>
        prev.map((p) =>
          p.id === productId
            ? {
                ...p,
                buy_later: true,
                buy_later_at: updated.buy_later_at,
                buy_later_notified: false,
              }
            : p
        )
      );
      toast.success(`Напомним купить через ${days} д.`);
    } catch (error) {
      toast.error('Не удалось добавить в отлежку');
    }
  };

  const removeBuyLater = async (productId) => {
    if (!user || !token) {
      toast.error('Войдите, чтобы управлять отлежкой');
      return;
    }
    try {
      await axios.patch(
        `${API}/products/${productId}/buy-later`,
        { buy_later: false },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (buyLaterOnly) {
        setProducts((prev) => prev.filter((p) => p.id !== productId));
      } else {
        setProducts((prev) =>
          prev.map((p) =>
            p.id === productId
              ? { ...p, buy_later: false, buy_later_at: null, buy_later_notified: false }
              : p
          )
        );
      }
      toast.success('Убрано из отлежки');
    } catch (error) {
      toast.error('Не удалось убрать из отлежки');
    }
  };

  const copyTextFallback = (text) => {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const selection = document.getSelection();
      const selectedRange = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
      const success = document.execCommand('copy');
      document.body.removeChild(textarea);
      if (selection && selectedRange) {
        selection.removeAllRanges();
        selection.addRange(selectedRange);
      }
      return success;
    } catch (error) {
      return false;
    }
  };

  const copyTextToClipboard = async (text) => {
    if (!text) return false;
    if (navigator?.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (error) {
        // fallback below
      }
    }
    if (window?.clipboardData?.setData) {
      try {
        window.clipboardData.setData('Text', text);
        return true;
      } catch (error) {
        // fallback below
      }
    }
    return copyTextFallback(text);
  };

  const copyProductInfo = async (product) => {
    const articleOrOem = product.oem || product.articul;
    const textToCopy = [articleOrOem, product.name].filter(Boolean).join(' ');
    if (!textToCopy) {
      toast.error('Нет данных для копирования');
      return;
    }
    try {
      const success = await copyTextToClipboard(textToCopy);
      if (success) {
        toast.success('Скопировано в буфер обмена');
      } else {
        throw new Error('copy failed');
      }
    } catch {
      toast.error('Не удалось скопировать');
    }
  };

  const buildShareFilterParams = () => {
    const filterParams = {};
    if (search) filterParams.q = search;
    if (inStockOnly) filterParams.inStock = '1';
    if (newOnly) filterParams.newOnly = '1';
    if (backOnStockOnly) filterParams.backOnStock = '1';
    if (sortBy && sortBy !== 'name') filterParams.sortBy = sortBy;
    if (sortOrder && sortOrder !== 'asc') filterParams.sortOrder = sortOrder;
    if (selectedSuppliers.length > 0) filterParams.suppliers = selectedSuppliers.join(',');
    if (selectedBrands.length > 0) filterParams.brands = selectedBrands.join(',');
    const interkomOnly = interkomSupplierId && selectedSuppliers.length === 1 && selectedSuppliers[0] === interkomSupplierId;
    if (selectedSegments.length > 0 && interkomOnly) filterParams.category = selectedSegments[0];
    return filterParams;
  };

  const buildSharePayload = (recipientName) => {
    const discountVal = parseInt(shareDiscount);
    const discountPercent = (!isNaN(discountVal) && discountVal >= 1 && discountVal <= 50) ? discountVal : null;
    const deliveryVal = parseInt(shareDeliveryDays);
    const deliveryDays = (!isNaN(deliveryVal) && deliveryVal >= 1 && deliveryVal <= 60) ? deliveryVal : null;
    const hasSelection = selectedItems.size > 0;
    return {
      params: hasSelection ? {} : buildShareFilterParams(),
      item_ids: hasSelection ? Array.from(selectedItems) : undefined,
      recipient_name: recipientName,
      recipient_phone: sharePhone.trim(),
      discount_percent: discountPercent,
      delivery_days: deliveryDays,
    };
  };

  const createShareLink = async (recipientName) => {
    setShareCreating(true);
    try {
      const discountVal = parseInt(shareDiscount);
      if (!isNaN(discountVal) && discountVal > 50) {
        toast.error('Максимальная скидка — 50%');
        setShareCreating(false);
        return;
      }
      const { data } = await axios.post(`${API}/public/share`, buildSharePayload(recipientName));
      const url = `${window.location.origin}/s/${data.code}`;
      const greeting = recipientName ? `${recipientName}, м` : 'М';
      const message = `${greeting}ы подобрали для вас интересующие запчасти, перейдите по ссылке ниже и выберите нужную вам запчасть.\n\n${url}`;
      const success = await copyTextToClipboard(message);
      if (success) {
        toast.success(recipientName
          ? `Ссылка для ${recipientName} скопирована!`
          : 'Короткая ссылка скопирована! Отправь клиенту в WhatsApp.');
      } else {
        throw new Error('copy failed');
      }
      setSharePopupOpen(false);
      setShareRecipient('');
      setSharePhone('');
      setSelectedItems(new Set());
    } catch {
      toast.error('Не удалось создать ссылку');
    } finally {
      setShareCreating(false);
    }
  };

  const sendShareViaWhatsApp = async () => {
    setShareWaSending(true);
    try {
      const discountVal = parseInt(shareDiscount);
      if (!isNaN(discountVal) && discountVal > 50) {
        toast.error('Максимальная скидка — 50%');
        return;
      }
      const { data } = await axios.post(`${API}/public/share`, buildSharePayload(shareRecipient.trim()));
      const url = `${window.location.origin}/s/${data.code}`;
      const greeting = shareRecipient.trim() ? `${shareRecipient.trim()}, м` : 'М';
      const message = `${greeting}ы подобрали для вас интересующие запчасти, перейдите по ссылке ниже и выберите нужную вам запчасть.\n\n${url}`;
      await axios.post(`${API}/whatsapp/send`, {
        phone: sharePhone.trim(),
        message,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setShareWaSent(true);
      setSelectedItems(new Set());
      toast.success(shareRecipient.trim()
        ? `Сообщение отправлено ${shareRecipient.trim()} в WhatsApp!`
        : 'Сообщение отправлено в WhatsApp!');
      setTimeout(() => {
        setSharePopupOpen(false);
        setShareRecipient('');
        setSharePhone('');
        setShareWaSent(false);
      }, 1200);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail ? `Ошибка: ${detail}` : 'Не удалось отправить в WhatsApp');
    } finally {
      setShareWaSending(false);
    }
  };

  const fetchProductDetails = async (productId) => {
    if (!productId) return null;
    const { data } = await axios.get(`${API}/products/${productId}`);
    setProducts((prev) => prev.map((p) => (p.id === productId ? { ...p, ...data } : p)));
    setSelectedProduct((prev) => (prev && prev.id === productId ? { ...prev, ...data } : prev));
    return data;
  };

  const openImageModal = (product) => {
    setActiveImageIndex(0);
    setSelectedProduct(product);
    setLinkedOffers([]);
    setLinkedBase(null);

    fetchProductDetails(product.id)
      .then((fullProduct) => {
        const productForImages = fullProduct || product;
        const shouldAutoFetch = !(
          productForImages.image_unavailable &&
          (!productForImages.images || productForImages.images.length === 0)
        );
        if (shouldAutoFetch) {
          fetchImagesForProduct(product.id);
        }
      })
      .catch((error) => {
        console.warn('Не удалось загрузить детали товара', error);
      });

    fetchLinkedOffers(product.id);
  };

  const fetchImagesForProduct = async (productId, options = {}) => {
    const { force = false, suppressToast = false } = options;
    // refresh-images требует админского доступа; для обычных пользователей выходим
    if (!user || !user.is_admin) {
      return;
    }
    setImageLoading(true);
    setImageProgress(0);
    try {
      const url = `${API}/products/${productId}/refresh-images${force ? '?force=true' : ''}`;
      const { data } = await axios.post(url, null, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setImageProgress(percent);
          }
        }
      });
      setProducts((prev) =>
        prev.map((p) =>
          p.id === productId
            ? {
                ...p,
                images: data.images || [],
                description: data.description || p.description,
                image_unavailable: data.image_unavailable ?? p.image_unavailable,
              }
            : p
        )
      );
      setSelectedProduct((prev) =>
        prev && prev.id === productId
          ? {
              ...prev,
              images: data.images || [],
              description: data.description || prev.description,
              image_unavailable: data.image_unavailable ?? prev.image_unavailable,
            }
          : prev
      );
    } catch (error) {
      const status = error?.response?.status;
      if (status === 403) {
        setProducts((prev) =>
          prev.map((p) =>
            p.id === productId ? { ...p, image_unavailable: true } : p
          )
        );
        setSelectedProduct((prev) =>
          prev && prev.id === productId ? { ...prev, image_unavailable: true } : prev
        );
      } else if (!suppressToast) {
        toast.error('Не удалось загрузить изображения');
      }
    } finally {
      setImageLoading(false);
      setImageProgress(0);
      imageRetryRef.current.delete(productId);
    }
  };

  const fetchLinkedOffers = async (productId) => {
    if (!productId) return;
    setLinkedLoading(true);
    try {
      const { data } = await axios.get(`${API}/products/${productId}/linked`, {
        params: getCatalogFilterParams(),
      });
      setLinkedOffers(data?.offers || []);
      setLinkedBase({
        id: data?.base_product_id,
        code: data?.base_product_code,
        name: data?.base_product_name,
        supplier_id: data?.base_supplier_id,
        supplier_name: data?.base_supplier_name,
      });
    } catch (error) {
      console.warn('Не удалось получить связанные предложения', error);
      setLinkedOffers([]);
      setLinkedBase(null);
    } finally {
      setLinkedLoading(false);
    }
  };

  const openProdAlternatives = async (product) => {
    setProdAltsProduct(product);
    setProdAltsOffers([]);
    setProdAltsLoading(true);
    setProdAltsModalOpen(true);
    try {
      const { data } = await axios.get(`${API}/products/${product.id}/linked`, {
        params: getCatalogFilterParams(),
      });
      const offers = (data?.offers || []).filter((o) => o.id !== product.id);
      offers.sort((a, b) => {
        const aStock = (a.in_stock || 0) > 0 ? 1 : 0;
        const bStock = (b.in_stock || 0) > 0 ? 1 : 0;
        if (bStock !== aStock) return bStock - aStock;
        return (a.client_price || 0) - (b.client_price || 0);
      });
      setProdAltsOffers(offers);
    } catch {
      toast.error('Не удалось загрузить альтернативы');
    } finally {
      setProdAltsLoading(false);
    }
  };

  const openMainAltModal = async (product) => {
    setAltOptions([]);
    setAltCurrentId(product.id);
    setAltMainLoading(true);
    setAltModalOpen(true);
    try {
      const { data } = await axios.post(
        `${API}/products/bulk-match`,
        {
          items: [{ name: '', articul: product.oem || product.articul, quantity: 1 }],
          ...getAltModalFilterBody(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const row = data?.results?.[0];
      const opts = [
        ...(row?.suggestion ? [row.suggestion] : []),
        ...(row?.alternatives || []),
      ];
      const unique = [];
      const seen = new Set();
      for (const p of opts) {
        if (p?.id && !seen.has(p.id)) {
          seen.add(p.id);
          unique.push(p);
        }
      }
      unique.sort((a, b) => {
        const aStock = (a.in_stock || 0) > 0 ? 1 : 0;
        const bStock = (b.in_stock || 0) > 0 ? 1 : 0;
        if (bStock !== aStock) return bStock - aStock;
        return (a.client_price || 0) - (b.client_price || 0);
      });
      setAltOptions(unique);
    } catch {
      toast.error('Не удалось загрузить альтернативы');
      setAltModalOpen(false);
    } finally {
      setAltMainLoading(false);
    }
  };

  const selectAlternative = (product) => {
    addToCart(product.id, product.packing_rate || 1);
    setAltCurrentId(product.id);
    setAltModalOpen(false);
  };

  const toggleAltWishList = async (product) => {
    if (!user || !token) { toast.error('Войдите, чтобы получать уведомления'); return; }
    const newVal = !product.wish_list;
    try {
      await axios.patch(
        `${API}/products/${product.id}/wish-list`,
        { wish_list: newVal },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAltOptions((prev) => prev.map((p) => p.id === product.id ? { ...p, wish_list: newVal } : p));
      toast.success(newVal ? 'Добавлено в ожидаемые' : 'Убрано из ожидаемых');
    } catch {
      toast.error('Не удалось обновить список ожидания');
    }
  };

  const refreshInterkomProduct = async (product) => {
    if (!user || !token) {
      toast.error('Войдите, чтобы обновить товар');
      return;
    }
    if (!user.is_admin) {
      toast.error('Обновление доступно только администратору');
      return;
    }
    if (interkomSupplierId && product.supplier_id !== interkomSupplierId) {
      toast.error('Обновление доступно только для товаров Интерком');
      return;
    }

    setRefreshingProducts((prev) => ({ ...prev, [product.id]: true }));
    try {
      const { data } = await axios.post(
        `${API}/admin/import/interkom/refresh`,
        {
          code: product.code,
          product_id: product.id,
          segment_id: product.segment_id,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (data?.product) {
        setProducts((prev) => prev.map((p) => (p.id === product.id ? { ...p, ...data.product } : p)));
        toast.success('Товар обновлён');
      } else {
        toast.success(data?.message || 'Обновление завершено');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Не удалось обновить товар');
    } finally {
      setRefreshingProducts((prev) => {
        const copy = { ...prev };
        delete copy[product.id];
        return copy;
      });
    }
  };

  const createOrder = async (supplierId) => {
    if (!user || !token) {
      toast.error('Войдите в систему');
      return;
    }

    try {
      const { data } = await axios.post(
        `${API}/orders`,
        { supplier_id: supplierId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data.supplier_name === 'Интерком') {
        toast.success('Заказ оформлен, отправка в Интерком выполняется в фоне');
      } else {
        toast.success('Заказ успешно оформлен!');
      }
      fetchCart(); // Refresh cart to remove ordered items
      fetchOpenOrders(); // Refresh markers for товаров в заказах
    } catch (error) {
      toast.error('Ошибка оформления заказа');
      console.error('Error creating order:', error);
    }
  };

  const openPriceHistory = (product) => {
    if (!product?.price_history || product.price_history.length === 0) {
      toast.info('История цен пока пуста');
      return;
    }
    setPriceHistoryProduct(product);
  };

  const formatCurrency = (value, currency) => {
    const rounded = Math.round(value || 0);
    return `${rounded.toLocaleString('ru-RU')} ${currency || 'KZT'}`;
  };

  const getPriceChangeIcon = (product) => {
    const hasHistory = product.price_history && product.price_history.length > 0;

    const currentClientPrice = product.client_price;
    const previousSupplierPrice = product.price_history?.[product.price_history.length - 2]?.price || 0;
    
    const rubRate = 5.5;
    const markupRate = 1.3;
    
    let previousClientPrice = previousSupplierPrice;
    if (product.supplier_currency === 'RUB') {
      previousClientPrice = previousSupplierPrice * rubRate;
    }
    previousClientPrice = previousClientPrice * markupRate;
    
    if (previousClientPrice <= 100) {
      previousClientPrice = Math.round(previousClientPrice / 10) * 10;
    } else if (previousClientPrice <= 1000) {
      previousClientPrice = Math.round(previousClientPrice / 50) * 50;
    } else if (previousClientPrice <= 10000) {
      previousClientPrice = Math.round(previousClientPrice / 100) * 100;
    } else {
      previousClientPrice = Math.round(previousClientPrice / 500) * 500;
    }
    
    const percentChange = previousClientPrice > 0 
      ? (((currentClientPrice - previousClientPrice) / previousClientPrice) * 100).toFixed(1) 
      : 0;
    
    const tooltipText = (
      <div className="text-sm">
        <p>Предыдущая: {previousClientPrice.toFixed(0)} ₸</p>
        <p>Изменение: {percentChange > 0 ? '+' : ''}{percentChange}%</p>
        {hasHistory && <p className="text-xs text-blue-600 mt-1">Нажмите, чтобы открыть историю цен</p>}
      </div>
    );

    let icon = <Minus className="w-4 h-4 text-gray-400" />;
    if (product.price_change === 'up') {
      icon = <ArrowUp className="w-4 h-4 text-red-500" />;
    } else if (product.price_change === 'down') {
      icon = <ArrowDown className="w-4 h-4 text-green-500" />;
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => hasHistory && openPriceHistory(product)}
            className={`p-1 rounded transition-colors ${hasHistory ? 'hover:bg-gray-100 cursor-pointer' : 'cursor-default'}`}
            disabled={!hasHistory}
          >
            {icon}
          </button>
        </TooltipTrigger>
        <TooltipContent>
          {hasHistory ? tooltipText : <p className="text-sm">История цен недоступна</p>}
        </TooltipContent>
      </Tooltip>
    );
  };

  const activeFiltersCount = 
    selectedSuppliers.length + 
    selectedSegments.length +
    selectedBrands.length +
    (inStockOnly ? 1 : 0) + 
    (newOnly ? 1 : 0) + 
    (backOnStockOnly ? 1 : 0) +
    (wishListOnly ? 1 : 0) +
    (buyLaterOnly ? 1 : 0);

  return (
    <TooltipProvider>
      <div
        className={
          embedded
            ? 'flex flex-1 flex-col min-h-0 overflow-hidden'
            : 'flex flex-col h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50'
        }
      >
        {!embedded && (
          <div className="bg-white border-b border-gray-200 shadow-sm px-6 py-3 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-xl">A</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>АвтоВАЗ.KZ</h1>
                <p className="text-xs text-gray-500">Запчасти для авто</p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {user ? (
                <>
                  <CrmNavLinks user={user} />
                  {user.is_admin && (
                    <Button variant="ghost" size="sm" onClick={() => window.location.href = '/admin'}>
                      Админ
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => window.location.href = '/orders'}>
                    Заказы
                  </Button>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">{user.name}</p>
                    <p className="text-xs text-gray-500">{user.email}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      logout();
                      window.location.href = '/login';
                    }}
                  >
                    Выход
                  </Button>
                </>
              ) : (
                <div className="flex items-center space-x-2">
                  <Button variant="ghost" size="sm" onClick={() => window.location.href = '/login'}>Вход</Button>
                  <Button size="sm" onClick={() => window.location.href = '/register'}>Регистрация</Button>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex flex-1 overflow-hidden">
          {/* Left Sidebar - Filters */}
          <div 
            className={`${sidebarCollapsed ? 'w-16' : 'w-64'} bg-white border-r border-gray-200 flex flex-col transition-all duration-300 shadow-lg`}
          >
            {/* Sidebar Header */}
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              {!sidebarCollapsed && (
                <div className="flex items-center gap-2">
                  <Filter className="w-5 h-5 text-blue-600" />
                  <h2 className="font-semibold text-gray-900">Фильтры</h2>
                  {activeFiltersCount > 0 && (
                    <span className="bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {activeFiltersCount}
                    </span>
                  )}
                </div>
              )}
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
              >
                {sidebarCollapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
              </button>
            </div>

            {/* Filters Content */}
            {!sidebarCollapsed && (
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Suppliers */}
                <div>
                  <Label className="font-semibold mb-2 block">Поставщики</Label>
                  <div className="space-y-2">
                    {suppliers.map((supplier) => (
                      <div key={supplier.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`supplier-${supplier.id}`}
                          checked={selectedSuppliers.includes(supplier.id)}
                          onCheckedChange={() => toggleSupplier(supplier.id)}
                        />
                        <Label
                          htmlFor={`supplier-${supplier.id}`}
                          className="cursor-pointer text-sm font-normal"
                        >
                          <div className="flex items-center gap-2">
                            <span>{supplier.name}</span>
                            {supplier.product_count > 0 && (
                              <span className="inline-flex items-center justify-center px-2 py-0.5 text-[10px] font-semibold bg-blue-100 text-blue-700 rounded-full">
                                {supplier.product_count}
                              </span>
                            )}
                          </div>
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border-t pt-4 space-y-2">
                  {interkomSupplierId && (
                    <div className="flex items-center justify-between">
                      <Label className="font-semibold">Сегменты (Интерком)</Label>
                      {!selectedSuppliers.every((id) => id === interkomSupplierId) && selectedSuppliers.length > 0 && (
                        <span className="text-[11px] text-gray-500">Только для Интерком</span>
                      )}
                    </div>
                  )}
                  {interkomSupplierId && (
                    <div className="space-y-2">
                      {interkomSegments.map((segment) => (
                        <div key={segment.name} className="flex items-center space-x-2">
                          <Checkbox
                            id={`seg-filter-${segment.name}`}
                            checked={selectedSegments.includes(segment.name)}
                            onCheckedChange={() => toggleSegment(segment.name)}
                            disabled={
                              selectedSuppliers.length > 0 && !selectedSuppliers.every((id) => id === interkomSupplierId)
                            }
                          />
                          <Label
                            htmlFor={`seg-filter-${segment.name}`}
                            className={`cursor-pointer text-sm font-normal ${selectedSuppliers.length > 0 && !selectedSuppliers.every((id) => id === interkomSupplierId) ? 'text-gray-400' : ''}`}
                          >
                            {segment.name}
                          </Label>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="border-t pt-4 space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="in-stock"
                        checked={inStockOnly}
                        onCheckedChange={(checked) => { setInStockOnly(checked); handleFilterChange(); }}
                      />
                      <Label htmlFor="in-stock" className="cursor-pointer text-sm font-normal">
                        В наличии
                        {filterCounts.in_stock > 0 && (
                          <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-[10px] font-semibold bg-blue-100 text-blue-700 rounded-full">
                            {filterCounts.in_stock}
                          </span>
                        )}
                      </Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="wish-list-only"
                        checked={wishListOnly}
                        onCheckedChange={(checked) => { setWishListOnly(checked); handleFilterChange(); }}
                      />
                      <Label htmlFor="wish-list-only" className="cursor-pointer text-sm font-normal">
                        Ожидаются
                        {filterCounts.wish_list > 0 && (
                          <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-[10px] font-semibold bg-blue-100 text-blue-700 rounded-full">
                            {filterCounts.wish_list}
                          </span>
                        )}
                      </Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="buy-later-only"
                        checked={buyLaterOnly}
                        onCheckedChange={(checked) => { setBuyLaterOnly(checked); handleFilterChange(); }}
                      />
                      <Label htmlFor="buy-later-only" className="cursor-pointer text-sm font-normal">
                        В отлежке
                        {filterCounts.buy_later > 0 && (
                          <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-[10px] font-semibold bg-orange-100 text-orange-700 rounded-full">
                            {filterCounts.buy_later}
                          </span>
                        )}
                      </Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="new-only"
                        checked={newOnly}
                        onCheckedChange={(checked) => { setNewOnly(checked); handleFilterChange(); }}
                      />
                      <Label htmlFor="new-only" className="cursor-pointer text-sm font-normal">
                        Новинки
                        {filterCounts.new > 0 && (
                          <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-[10px] font-semibold bg-blue-100 text-blue-700 rounded-full">
                            {filterCounts.new}
                          </span>
                        )}
                      </Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="back-on-stock"
                        checked={backOnStockOnly}
                        onCheckedChange={(checked) => { setBackOnStockOnly(checked); handleFilterChange(); }}
                      />
                      <Label htmlFor="back-on-stock" className="cursor-pointer text-sm font-normal">
                        Появились на складе
                        {filterCounts.back_on_stock > 0 && (
                          <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-[10px] font-semibold bg-blue-100 text-blue-700 rounded-full">
                            {filterCounts.back_on_stock}
                          </span>
                        )}
                      </Label>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <Label className="font-semibold mb-2 block">Бренды</Label>
                    <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                      {brandOptions.map((brand) => (
                        <div key={brand} className="flex items-center space-x-2">
                          <Checkbox
                            id={`brand-${brand}`}
                            checked={selectedBrands.includes(brand)}
                            onCheckedChange={() => toggleBrand(brand)}
                          />
                          <Label
                            htmlFor={`brand-${brand}`}
                            className="cursor-pointer text-sm font-normal"
                          >
                            {brand}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Clear Filters - at the bottom */}
                  {activeFiltersCount > 0 && (
                    <Button variant="outline" size="sm" onClick={clearFilters} className="w-full mt-4">
                      <X className="w-4 h-4 mr-2" />
                      Очистить все
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>



          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Search Bar */}
            <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 shadow-sm">
              <div className="relative flex-1 max-w-3xl">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  placeholder="Поиск по названию, артикулу, OEM..."
                  value={searchInput}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  ref={searchInputRef}
                  className="pl-9 pr-10"
                />
                {searchInput && (
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      handleSearchClear();
                      searchInputRef.current?.focus();
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 z-10 rounded-full p-2 text-gray-400 hover:text-gray-600"
                    aria-label="Очистить поиск"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <Button variant="outline" onClick={() => setBulkModalOpen(true)}>
                Массовый заказ
              </Button>
              {search && (
                <div className="relative">
                  <Button
                    variant="outline"
                    onClick={() => {
                      const isInterkomOnly = interkomSupplierId && selectedSuppliers.length === 1 && selectedSuppliers[0] === interkomSupplierId;
                      const isNoSupplier = selectedSuppliers.length === 0;
                      setShareDeliveryDays(isInterkomOnly || isNoSupplier ? '1' : '');
                      setSharePopupOpen((o) => !o);
                    }}
                    title="Поделиться подборкой с клиентом"
                  >
                    <Share2 className="w-4 h-4 mr-1.5" />
                    Поделиться
                    {selectedItems.size > 0 && (
                      <span className="ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 text-white text-xs font-bold">
                        {selectedItems.size}
                      </span>
                    )}
                  </Button>
                  {sharePopupOpen && (
                    <div className="absolute right-0 top-full mt-2 z-50 bg-white border border-gray-200 rounded-xl shadow-lg p-4 w-80">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-semibold text-gray-800">
                          {selectedItems.size > 0
                            ? `Поделиться: ${selectedItems.size} позиц${selectedItems.size === 1 ? 'ия' : selectedItems.size >= 2 && selectedItems.size <= 4 ? 'ии' : 'ий'}`
                            : 'Поделиться подборкой'}
                        </span>
                        <button
                          onClick={() => { setSharePopupOpen(false); setShareRecipient(''); setSharePhone(''); setShareWaSent(false); }}
                          className="text-gray-400 hover:text-gray-600 text-lg leading-none"
                        >✕</button>
                      </div>

                      {/* Selection mode hint */}
                      {selectedItems.size > 0 ? (
                        <div className="mb-3 flex items-center justify-between gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                          <span className="text-xs text-blue-700 font-medium">
                            Выбрано {selectedItems.size} позиц{selectedItems.size === 1 ? 'ия' : selectedItems.size >= 2 && selectedItems.size <= 4 ? 'ии' : 'ий'}
                          </span>
                          <button
                            type="button"
                            onClick={() => setSelectedItems(new Set())}
                            className="text-xs text-blue-500 hover:text-blue-700 underline flex-shrink-0"
                          >сбросить</button>
                        </div>
                      ) : (
                        <div className="mb-3 text-xs text-gray-400">
                          Будет отправлена вся подборка. Отметьте позиции в таблице, чтобы поделиться конкретными товарами.
                        </div>
                      )}

                      {/* Recipient name */}
                      <div className="mb-3">
                        <label className="text-xs text-gray-500 mb-1 block">Кому отправляете?</label>
                        <input
                          autoFocus
                          type="text"
                          placeholder="Имя клиента (необязательно)"
                          value={shareRecipient}
                          onChange={(e) => setShareRecipient(e.target.value)}
                          className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      {/* Phone */}
                      <div className="mb-3">
                        <label className="text-xs text-gray-500 mb-1 block">Телефон клиента</label>
                        <input
                          type="tel"
                          placeholder="+7 777 123 45 67"
                          value={sharePhone}
                          onChange={(e) => { setSharePhone(e.target.value); setShareWaSent(false); }}
                          className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      {/* Delivery days */}
                      <div className="mb-3">
                        <label className="text-xs text-gray-500 mb-1 block">Срок поставки</label>
                        <div className="flex items-center gap-2">
                          <div className="relative w-24">
                            <input
                              type="number"
                              min="1"
                              max="60"
                              placeholder="дней"
                              value={shareDeliveryDays}
                              onChange={(e) => setShareDeliveryDays(e.target.value)}
                              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 pr-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <span className="text-sm text-gray-500">
                            {shareDeliveryDays === '1'
                              ? 'день — следующий день'
                              : shareDeliveryDays === '2' || shareDeliveryDays === '3' || shareDeliveryDays === '4'
                                ? `${shareDeliveryDays} дня`
                                : shareDeliveryDays
                                  ? `${shareDeliveryDays} дней`
                                  : 'дней (укажите срок)'}
                          </span>
                        </div>
                        <div className="flex gap-1.5 mt-1.5">
                          {[['1 день', '1'], ['2 дня', '2'], ['3 дня', '3'], ['5 дней', '5'], ['7 дней', '7']].map(([label, val]) => (
                            <button key={val} type="button" onClick={() => setShareDeliveryDays(val)}
                              className={`px-2 py-0.5 rounded-full text-xs font-medium border transition-colors ${
                                shareDeliveryDays === val
                                  ? 'bg-blue-600 text-white border-blue-600'
                                  : 'bg-white text-gray-600 border-gray-200 hover:border-blue-400'
                              }`}>{label}</button>
                          ))}
                        </div>
                      </div>

                      {/* Discount field */}
                      <div className="mb-3">
                        <label className="text-xs text-gray-500 mb-1 block">Скидка для клиента</label>
                        <div className="flex items-center gap-2 mb-2">
                          <div className="relative w-24">
                            <input
                              type="number"
                              min="0"
                              max="50"
                              placeholder="0"
                              value={shareDiscount}
                              onChange={(e) => setShareDiscount(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && createShareLink(shareRecipient)}
                              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 pr-7 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">%</span>
                          </div>
                          <span className="text-xs text-gray-400">Макс. 50%</span>
                        </div>
                        {/* Quick chips */}
                        <div className="flex flex-wrap gap-1.5">
                          {[['Без скидки', ''], ['10%', '10'], ['12%', '12'], ['15%', '15'], ['20%', '20']].map(([label, val]) => (
                            <button
                              key={label}
                              type="button"
                              onClick={() => setShareDiscount(val)}
                              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                                shareDiscount === val
                                  ? 'bg-blue-600 text-white border-blue-600'
                                  : 'bg-white text-gray-600 border-gray-200 hover:border-blue-400 hover:text-blue-600'
                              }`}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                        <p className="text-xs text-gray-400 mt-1.5">Оставьте пустым, если скидка не нужна</p>
                      </div>

                      <div className="flex flex-col gap-2">
                        {sharePhone.trim() && (
                          <button
                            type="button"
                            onClick={sendShareViaWhatsApp}
                            disabled={shareWaSending || shareWaSent}
                            className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                              shareWaSent
                                ? 'bg-green-100 text-green-700 border border-green-300 cursor-default'
                                : 'bg-[#25D366] hover:bg-[#1ebe5d] text-white disabled:opacity-60'
                            }`}
                          >
                            {shareWaSent ? (
                              <>✓ Отправлено</>
                            ) : shareWaSending ? (
                              'Отправляем...'
                            ) : (
                              <>
                                <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current flex-shrink-0"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.117.552 4.103 1.518 5.829L0 24l6.335-1.518A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.793 9.793 0 01-4.998-1.366l-.358-.213-3.758.984.999-3.662-.233-.375A9.78 9.78 0 012.182 12C2.182 6.57 6.57 2.182 12 2.182S21.818 6.57 21.818 12 17.43 21.818 12 21.818z"/></svg>
                                Отправить в WhatsApp
                              </>
                            )}
                          </button>
                        )}
                        <Button
                          size="sm"
                          onClick={() => createShareLink(shareRecipient)}
                          disabled={shareCreating}
                          className="w-full"
                        >
                          {shareCreating ? 'Создаём...' : 'Скопировать ссылку'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Products Table */}
            <div className="flex-1 overflow-auto bg-white">
              <table className="w-full">
              <thead className="sticky top-0 z-20 bg-gray-50 border-b-2 border-gray-200 shadow-sm">
                <tr className="bg-gray-50">
                  <th className="px-2 py-3 text-center w-10 bg-gray-50">
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 cursor-pointer"
                      checked={products.length > 0 && products.every(p => selectedItems.has(p.id))}
                      ref={el => { if (el) el.indeterminate = selectedItems.size > 0 && !products.every(p => selectedItems.has(p.id)); }}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedItems(prev => new Set([...prev, ...products.map(p => p.id)]));
                        } else {
                          setSelectedItems(prev => {
                            const next = new Set(prev);
                            products.forEach(p => next.delete(p.id));
                            return next;
                          });
                        }
                      }}
                      title="Выбрать / снять все на странице"
                    />
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 w-16 bg-gray-50">П</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 w-32 bg-gray-50">Бренд</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 w-32 bg-gray-50">Артикул</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 w-32 bg-gray-50">OEM</th>
                  <th 
                    className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 bg-gray-50"
                    onClick={() => handleSortChange('name')}
                  >
                    <div className="flex items-center gap-2">
                      Наименование
                      {sortBy === 'name' && (sortOrder === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />)}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 w-20 bg-gray-50">ЕИ</th>
                  <th 
                    className="px-4 py-3 text-center text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 w-24 bg-gray-50"
                    onClick={() => handleSortChange('in_stock')}
                  >
                    <div className="flex items-center justify-center gap-2">
                      Остаток
                      {sortBy === 'in_stock' && (sortOrder === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />)}
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-center text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 w-36 bg-gray-50"
                    onClick={() => handleSortChange('client_price')}
                  >
                    <div className="flex items-center justify-center gap-2">
                      Цена
                      {sortBy === 'client_price' && (sortOrder === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />)}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 w-48 bg-gray-50">Добавить</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {products.map((product, index) => {
                  const isLastProduct = products.length === index + 1;
                  const showSupplierPrice = showPriceType[product.id];
                  const quantity = quantities[product.id] || product.packing_rate || 1;
                  const supplierInitial = product.supplier_name?.charAt(0).toUpperCase() || '?';
                  const rowHighlight = product.buy_later
                    ? 'bg-orange-50/70 border-l-4 border-orange-400'
                    : product.wish_list
                      ? 'bg-yellow-50/70 border-l-4 border-yellow-400'
                      : '';
                  const hasImages = product.images && product.images.length > 0;
                  const inCart = cartProductQtyMap.has(product.id);
                  const cartQty = cartProductQtyMap.get(product.id);
                  const inCustomerOrder = customerOrderProductQtyMap.has(product.id);
                  const customerOrderQty = customerOrderProductQtyMap.get(product.id);
                  const inOpenOrder = openOrderProductMap.has(product.id);
                  const openOrderInfo = openOrderProductMap.get(product.id);

                  const isSelected = selectedItems.has(product.id);

                  return (
                    <tr
                      key={product.id}
                      ref={isLastProduct ? lastProductRef : null}
                      className={`hover:bg-blue-50/50 transition-colors group ${isSelected ? 'bg-blue-50/60' : rowHighlight}`}
                    >
                      <td className="px-2 py-2 text-center">
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 cursor-pointer"
                          checked={isSelected}
                          onChange={() => {
                            setSelectedItems(prev => {
                              const next = new Set(prev);
                              if (next.has(product.id)) next.delete(product.id);
                              else next.add(product.id);
                              return next;
                            });
                          }}
                        />
                      </td>
                      <td className="px-4 py-2 text-center">
                        <div
                          className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center mx-auto text-white font-bold text-sm"
                          title={product.supplier_name}
                        >
                          {supplierInitial}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-sm">
                        <span className="text-gray-900">{product.brand || '-'}</span>
                      </td>
                      <td className="px-4 py-2 text-sm">
                        <span className="text-gray-900 font-mono text-xs">{product.articul || '-'}</span>
                      </td>
                      <td className="px-4 py-2 text-sm">
                        <span className="text-gray-900 font-mono text-xs">{product.oem || '-'}</span>
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-900 relative">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            className="text-left hover:text-indigo-700 transition-colors"
                            onClick={() => openImageModal(product)}
                          >
                            {product.name}
                          </button>
                          {product.new && (
                            <span className="text-[10px] font-semibold text-green-800 bg-green-100 px-2 py-0.5 rounded-full">
                              Новинка
                            </span>
                          )}
                          {product.back_on_stock && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="text-[10px] font-semibold text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded-full cursor-help">
                                  Появился
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Появился на складе</p>
                                <StockStatusDateLine isoDate={product.back_on_stock_at} />
                              </TooltipContent>
                            </Tooltip>
                          )}
                          {product.wish_list && (
                            <span className="text-[10px] font-semibold text-yellow-800 bg-yellow-100 px-2 py-0.5 rounded-full">
                              Ожидается
                            </span>
                          )}
                          {product.buy_later && (
                            <span className="text-[10px] font-semibold text-orange-800 bg-orange-100 px-2 py-0.5 rounded-full">
                              {product.buy_later_at
                                ? `Купить ${new Date(product.buy_later_at).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}`
                                : 'В отлежке'}
                            </span>
                          )}
                          {product.has_cheaper_alt && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded-full border border-violet-200">
                              {product.cheaper_alt_price != null
                                ? `Есть дешевле за ${formatBadgePrice(product.cheaper_alt_price)}`
                                : 'Есть дешевле'}
                            </span>
                          )}
                          {product.alt_count > 0 && (
                            <button
                              type="button"
                              onClick={() => openMainAltModal(product)}
                              className="inline-flex items-center gap-1 text-[10px] font-semibold text-sky-800 bg-sky-100 px-1.5 py-0.5 rounded-full border border-sky-200 hover:bg-sky-200 transition-colors"
                              title="Показать альтернативы другого бренда/цены"
                            >
                              <Shuffle className="w-3 h-3" />
                              ещё {product.alt_count}
                            </button>
                          )}
                        </div>
                        {(inCart || inCustomerOrder || inOpenOrder) && (
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            {inCart && (
                              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-800 bg-blue-100 px-2 py-0.5 rounded-full border border-blue-200">
                                <ShoppingCart className="w-3 h-3" />
                                В закупке{cartQty ? ` × ${cartQty}` : ''}
                              </span>
                            )}
                            {inCustomerOrder && (
                              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-green-800 bg-green-100 px-2 py-0.5 rounded-full border border-green-200">
                                <UserPlus className="w-3 h-3" />
                                В заказе клиента{customerOrderQty ? ` × ${customerOrderQty}` : ''}
                              </span>
                            )}
                            {inOpenOrder && (
                              <button
                                type="button"
                                onClick={() => {
                                  const q = openOrderInfo?.order_number || openOrderInfo?.order_id || '';
                                  window.location.href = `/orders${q ? `?q=${encodeURIComponent(q)}` : ''}`;
                                }}
                                className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-800 bg-indigo-100 px-2 py-0.5 rounded-full border border-indigo-200 hover:bg-indigo-200 transition-colors cursor-pointer"
                              >
                                <Truck className="w-3 h-3" />
                                В заказе{openOrderInfo?.quantity ? ` × ${openOrderInfo.quantity}` : ''}
                              </button>
                            )}
                          </div>
                        )}
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 hidden group-hover:flex items-center bg-white shadow-sm border rounded-full overflow-hidden">
                          {interkomSupplierId && product.supplier_id === interkomSupplierId && (
                            <button
                              className="p-1.5 hover:bg-gray-100 border-r text-gray-600 disabled:opacity-60 disabled:cursor-not-allowed"
                              onClick={() => refreshInterkomProduct(product)}
                              disabled={!!refreshingProducts[product.id]}
                              title="Обновить"
                            >
                              <RotateCcw className={`w-4 h-4 ${refreshingProducts[product.id] ? 'animate-spin' : ''}`} />
                            </button>
                          )}
                          <button
                            className="p-1.5 hover:bg-gray-100 border-r text-gray-600"
                            onClick={() => copyProductInfo(product)}
                            title="Скопировать артикул и наименование"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          <button
                            className="p-1.5 hover:bg-gray-100 border-r text-gray-600"
                            onClick={() => openMainAltModal(product)}
                            title="Альтернативы (другой бренд/цена)"
                          >
                            <Shuffle className="w-4 h-4" />
                          </button>
                          <button
                            className="p-1.5 hover:bg-gray-100 border-r text-gray-600"
                            onClick={() => openProdAlternatives(product)}
                            title="Альтернативы от других поставщиков"
                          >
                            <Layers className="w-4 h-4" />
                          </button>
                          <BuyLaterButton
                            product={product}
                            disabled={!user}
                            onSetDays={(days) => setBuyLater(product.id, days)}
                            onRemove={() => removeBuyLater(product.id)}
                          />
                          <button
                            className={`p-1.5 hover:bg-gray-100 text-gray-600 ${product.wish_list ? 'bg-blue-50 text-blue-600' : ''}`}
                            onClick={() => toggleWishList(product.id, product.wish_list)}
                            title={product.wish_list ? "Убрать из ожидаемых (сотрудники)" : "В ожидаемые (сотрудники)"}
                          >
                            <Bell className="w-4 h-4" />
                          </button>
                          {canEditCrm && (
                            <button
                              className="p-1.5 hover:bg-amber-50 text-amber-700 border-l"
                              onClick={() => setNotifyClientProduct(product)}
                              title="Сообщить клиенту, когда появится"
                            >
                              <BellRing className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-sm text-center text-gray-700">{product.unit}</td>
                      <td className="px-4 py-2 text-sm text-center">
                        {product.in_stock > 0 ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mx-auto cursor-help">
                                <Check className="w-4 h-4 text-green-600" />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>В наличии: {product.in_stock} {product.unit}</p>
                              <StockStatusDateLine isoDate={product.back_on_stock_at} />
                            </TooltipContent>
                          </Tooltip>
                        ) : product.in_transit ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center mx-auto cursor-help">
                                <Truck className="w-4 h-4 text-blue-600" />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>В пути на склад</p>
                              <StockStatusDateLine isoDate={product.in_transit_at} />
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center mx-auto cursor-help">
                                <XIcon className="w-4 h-4 text-red-600" />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Нет в наличии</p>
                              <StockStatusDateLine isoDate={product.out_of_stock_at} />
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </td>
                      <td className="px-4 py-2 text-sm text-center">
                        <div className="flex items-center justify-center gap-1">
                          {getPriceChangeIcon(product)}
                          <button
                            onClick={() => togglePriceType(product.id)}
                            className="hover:bg-gray-100 rounded px-1 py-0.5"
                          >
                            <p className="font-bold text-sm text-gray-900 whitespace-nowrap">
                              {showSupplierPrice
                                ? `${product.supplier_price.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} ${product.supplier_currency}`
                                : `${product.client_price.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} ₸`}
                            </p>
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-1 justify-center">
                          <Input
                            type="number"
                            min={product.packing_rate || 1}
                            step={product.packing_rate || 1}
                            value={quantity}
                            onChange={(e) => {
                              const val = parseInt(e.target.value) || product.packing_rate || 1;
                              const packingRate = product.packing_rate || 1;
                              const rounded = Math.max(packingRate, Math.round(val / packingRate) * packingRate);
                              setQuantities(prev => ({ ...prev, [product.id]: rounded }));
                            }}
                            className="w-14 h-7 text-center text-xs"
                          />
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                onClick={() => addToCart(product.id, quantity)}
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 border-blue-300 text-blue-700 hover:bg-blue-50"
                                disabled={product.in_stock === 0}
                              >
                                <ShoppingCart className="w-3 h-3" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Добавить в корзину поставщика</p>
                            </TooltipContent>
                          </Tooltip>
                          {canEditCrm && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  onClick={() => addToCustomerOrder(product, quantity)}
                                  size="sm"
                                  className="h-7 px-2 bg-green-600 hover:bg-green-700 text-white"
                                >
                                  <UserPlus className="w-3 h-3" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Добавить в заказ клиента</p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {loading && (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            )}
          </div>

          {/* Bottom panel: supplier carts + customer order draft */}
          {showBottomPanel && (
            <div className="bg-white border-t-2 border-gray-300 shadow-2xl flex-shrink-0" style={{ height: '420px', display: 'flex', flexDirection: 'column' }}>
              <Tabs
                value={bottomPanelTab}
                onValueChange={setBottomPanelTab}
                className="w-full h-full"
                style={{ display: 'flex', flexDirection: 'column' }}
              >
                <TabsList
                  className="w-full justify-start border-b rounded-none bg-gradient-to-r from-slate-50 to-gray-100 px-4 flex-shrink-0"
                  style={{ height: '48px' }}
                >
                  {hasSupplierCart && (
                    <TabsTrigger value="suppliers" className="px-6 font-semibold">
                      Корзины поставщиков ({cart.items.length})
                    </TabsTrigger>
                  )}
                  {canEditCrm && (
                    <TabsTrigger value="customer-order" className="px-6 font-semibold data-[state=active]:text-green-800">
                      Заказ клиента ({customerOrderDraft.items.length})
                    </TabsTrigger>
                  )}
                </TabsList>

                <div style={{ flex: 1, overflow: 'hidden' }}>
                  {bottomPanelTab === 'suppliers' && hasSupplierCart && (
                    <Tabs
                      value={activeCartTab}
                      onValueChange={setActiveCartTab}
                      className="w-full h-full"
                      style={{ display: 'flex', flexDirection: 'column' }}
                    >
                      <TabsList className="w-full justify-start border-b rounded-none bg-gradient-to-r from-blue-50 to-indigo-50 px-4 flex-shrink-0" style={{ height: '40px' }}>
                        {Object.keys(cartsBySupplier).map((supplierName) => (
                          <TabsTrigger key={supplierName} value={supplierName} className="px-4 text-sm font-semibold">
                            {supplierName} ({cartsBySupplier[supplierName].length})
                          </TabsTrigger>
                        ))}
                      </TabsList>

                      <div style={{ flex: 1, overflow: 'hidden' }}>
                        {Object.keys(cartsBySupplier).map((supplierName) => {
                          const items = cartsBySupplier[supplierName];
                          const total = items.reduce((sum, item) => sum + (showPurchasePrice ? item.supplier_subtotal : item.subtotal), 0);
                          const totalQty = items.reduce((sum, item) => sum + item.quantity, 0);

                          if (supplierName !== activeCartTab) return null;

                          return (
                            <div
                              key={supplierName}
                              className="h-full"
                              style={{ display: 'flex', flexDirection: 'column' }}
                            >
                              <div style={{ flex: 1, overflowY: 'auto', paddingLeft: '1rem', paddingRight: '1rem', paddingTop: '0.5rem' }}>
                                <table className="w-full text-sm">
                                  <thead className="bg-gray-100 sticky top-0 z-10">
                                    <tr>
                                      <th className="px-2 py-2 text-left font-semibold text-gray-700">Артикул</th>
                                      <th className="px-2 py-2 text-left font-semibold text-gray-700">Наименование</th>
                                      <th className="px-2 py-2 text-center font-semibold text-gray-700">Количество</th>
                                      <th className="px-2 py-2 text-right font-semibold text-gray-700">Цена</th>
                                      <th className="px-2 py-2 text-right font-semibold text-gray-700">Сумма</th>
                                      <th className="px-2 py-2 text-center w-16"></th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-100">
                                    {items.map((item) => (
                                      <tr key={item.product_id} className="hover:bg-blue-50/30">
                                        <td className="px-2 py-2 text-gray-800 font-mono text-xs">{item.product_code}</td>
                                        <td className="px-2 py-2 text-gray-900">{item.product_name}</td>
                                        <td className="px-2 py-2 text-center">
                                          <Input
                                            type="number"
                                            value={item.quantity}
                                            onChange={(e) => updateCartItem(item.product_id, parseInt(e.target.value) || 1)}
                                            className="w-16 h-7 text-center mx-auto text-sm"
                                            min="1"
                                          />
                                        </td>
                                        <td className="px-2 py-2 text-right text-gray-800">
                                          {showPurchasePrice
                                            ? `${item.supplier_price.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} ₸`
                                            : `${item.price.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} ₸`}
                                        </td>
                                        <td className="px-2 py-2 text-right font-semibold text-gray-900">
                                          {showPurchasePrice
                                            ? `${item.supplier_subtotal.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} ₸`
                                            : `${item.subtotal.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} ₸`}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => removeFromCart(item.product_id)}
                                            className="h-7 w-7 p-0 hover:bg-red-50"
                                          >
                                            <Trash2 className="w-4 h-4 text-red-500" />
                                          </Button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>

                              <div className="border-t-2 border-gray-300 bg-gradient-to-r from-gray-50 to-blue-50 px-4 py-3" style={{ flexShrink: 0, height: '80px' }}>
                                <div className="flex justify-between items-center">
                                  <div className="flex items-center gap-6">
                                    <div>
                                      <span className="text-sm text-gray-600">Количество:</span>
                                      <span className="ml-2 font-bold text-lg text-gray-900">{totalQty} шт</span>
                                    </div>
                                    <div>
                                      <span className="text-sm text-gray-600">
                                        {showPurchasePrice ? 'Закупочная цена:' : 'Продажная цена:'}
                                      </span>
                                      <button
                                        onClick={() => setShowPurchasePrice(!showPurchasePrice)}
                                        className="ml-2 font-bold text-xl text-blue-600 hover:text-blue-800 transition-colors cursor-pointer underline decoration-dotted"
                                        title="Нажмите для переключения типа цены"
                                      >
                                        {total.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} ₸
                                      </button>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-4">
                                    <div className="text-xs text-gray-500 italic">
                                      {showPurchasePrice ? '(Нажмите на сумму для продажной цены)' : '(Нажмите на сумму для закупочной цены)'}
                                    </div>
                                    <Button
                                      onClick={() => createOrder(items[0].supplier_id)}
                                      className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold px-6 shadow-lg"
                                    >
                                      Оформить заказ
                                    </Button>
                                    <Button
                                      variant="outline"
                                      onClick={clearCart}
                                      className="border-gray-300 text-gray-700 hover:bg-gray-100"
                                    >
                                      Очистить корзину
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </Tabs>
                  )}

                  {bottomPanelTab === 'customer-order' && canEditCrm && (
                    <CustomerOrderDraftPanel
                      token={token}
                      draft={customerOrderDraft}
                      onDraftChange={setCustomerOrderDraft}
                      onDraftReset={resetCustomerOrderDraft}
                    />
                  )}
                </div>
              </Tabs>
            </div>
          )}
          </div>
        </div>

        {/* ── Extracted modals ── */}
        <BulkMatchModal
          open={bulkModalOpen}
          onClose={() => setBulkModalOpen(false)}
          cartProductQtyMap={cartProductQtyMap}
          openOrderProductMap={openOrderProductMap}
          catalogFilterBody={getBulkMatchFilterBody()}
          onAddToCart={addToCart}
          onCartRefresh={fetchCart}
          user={user}
          token={token}
        />

        <AltModal
          open={altModalOpen}
          onClose={() => setAltModalOpen(false)}
          options={altOptions}
          currentId={altCurrentId}
          mode="main"
          loading={altMainLoading}
          onSelect={selectAlternative}
          onToggleWishList={toggleAltWishList}
        />

        <ProdAltsModal
          open={prodAltsModalOpen}
          onClose={() => setProdAltsModalOpen(false)}
          product={prodAltsProduct}
          offers={prodAltsOffers}
          loading={prodAltsLoading}
          onAddToCart={addToCart}
        />

        <PriceHistoryModal
          product={priceHistoryProduct}
          onClose={() => setPriceHistoryProduct(null)}
        />

        {selectedProduct && (
          <ProductDetailModal
            product={selectedProduct}
            onClose={() => {
              setSelectedProduct(null);
              setLinkedOffers([]);
              setLinkedBase(null);
              setLinkedLoading(false);
            }}
            imageLoading={imageLoading}
            imageProgress={imageProgress}
            onRefreshImages={fetchImagesForProduct}
            onSearchImage={(product) => setImageSearchProduct(product)}
            isAdmin={user?.is_admin}
            linkedOffers={linkedOffers}
            linkedBase={linkedBase}
            linkedLoading={linkedLoading}
            imageRetryRef={imageRetryRef}
            onAddToCart={addToCart}
            onToggleWishList={toggleWishList}
            onNotifyClient={canEditCrm ? setNotifyClientProduct : undefined}
            onProductUpdated={(updated) => {
              setProducts((prev) =>
                prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p))
              );
              setSelectedProduct((prev) =>
                prev && prev.id === updated.id ? { ...prev, ...updated } : prev
              );
            }}
            user={user}
            token={token}
          />
        )}

        <NotifyClientStockDialog
          open={!!notifyClientProduct}
          onOpenChange={(open) => {
            if (!open) setNotifyClientProduct(null);
          }}
          token={token}
          product={notifyClientProduct}
          onCreated={() => {
            if (notifyClientProduct?.id) {
              const productId = notifyClientProduct.id;
              setProducts((prev) =>
                prev.map((p) => (p.id === productId ? { ...p, wish_list: true } : p))
              );
              setSelectedProduct((prev) =>
                prev && prev.id === productId ? { ...prev, wish_list: true } : prev
              );
            }
          }}
        />

        {imageSearchProduct && (
          <ImageSearchModal
            product={imageSearchProduct}
            token={token}
            onClose={() => setImageSearchProduct(null)}
            onApplied={(productId, data) => {
              setProducts((prev) =>
                prev.map((p) =>
                  p.id === productId
                    ? { ...p, images: data.images, image_unavailable: false }
                    : p
                )
              );
              setSelectedProduct((prev) =>
                prev && prev.id === productId
                  ? { ...prev, images: data.images, image_unavailable: false }
                  : prev
              );
              toast.success('Картинка успешно сохранена');
            }}
          />
        )}


      </div>
      
    </TooltipProvider>
    
  );
};

export default HomePageNew;
